import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';

export const Dashboard: React.FC = () => {
  const { user, userRole } = useAuth();
  const [stats, setStats] = useState<any>(null);
  const [myCourses, setMyCourses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboardData = async () => {
      if (!user?.tenant_id) return;

      try {
        setLoading(true);
        
        // Load apprentices (filtered by role)
        const instructorEmail = userRole === 'instructor' ? user.email : undefined;
        const apprenticesRes = await axios.get(
          `/api/apprentices/tenant/${user.tenant_id}`,
          { params: instructorEmail ? { instructor_email: instructorEmail } : {} }
        );
        const apprentices = apprenticesRes.data;

        // Calculate stats
        const activeApprentices = apprentices.filter((a: any) => a.status === 'active').length;
        const completedApprentices = apprentices.filter((a: any) => a.status === 'completed').length;

        let avgProgress = 0;
        if (apprentices.length > 0) {
          const totalProgress = apprentices.reduce((sum: number, apprentice: any) => {
            const progress = Number(apprentice.otj_percentage) || 0;
            return sum + progress;
          }, 0);
          avgProgress = Math.round(totalProgress / apprentices.length);
        }

        setStats({
          totalApprentices: apprentices.length,
          activeApprentices,
          completedApprentices,
          avgProgress,
        });

        // For instructors, group by standard to show courses
        if (userRole === 'instructor') {
          const courseMap = new Map();
          apprentices.forEach((apprentice: any) => {
            if (apprentice.standard) {
              const key = apprentice.standard.reference;
              if (!courseMap.has(key)) {
                courseMap.set(key, {
                  reference: apprentice.standard.reference,
                  title: apprentice.standard.title,
                  level: apprentice.standard.level,
                  apprenticeCount: 0,
                  apprentices: [],
                });
              }
              const course = courseMap.get(key);
              course.apprenticeCount++;
              course.apprentices.push(apprentice);
            }
          });
          setMyCourses(Array.from(courseMap.values()));
        }
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [user?.tenant_id, user?.email, userRole]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {userRole === 'instructor' ? 'My Dashboard' : 'Dashboard'}
        </h1>
        <p className="text-gray-600">
          {userRole === 'instructor' 
            ? 'Overview of your caseload and programmes'
            : 'Overview of apprenticeship tracking system'}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500 mb-2">
            {userRole === 'instructor' ? 'My Apprentices' : 'Total Apprentices'}
          </div>
          <div className="text-3xl font-bold text-blue-600">{stats?.totalApprentices || 0}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500 mb-2">Active</div>
          <div className="text-3xl font-bold text-green-600">{stats?.activeApprentices || 0}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500 mb-2">Completed</div>
          <div className="text-3xl font-bold text-purple-600">{stats?.completedApprentices || 0}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500 mb-2">Avg Progress</div>
          <div className="text-3xl font-bold text-orange-600">{stats?.avgProgress || 0}%</div>
        </div>
      </div>

      {/* My Courses/Programmes - For Instructors */}
      {userRole === 'instructor' && myCourses.length > 0 && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">My Programmes</h2>
          <div className="grid gap-4">
            {myCourses.map((course) => (
              <div key={course.reference} className="bg-white rounded-lg shadow p-6 hover:shadow-md transition">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{course.title}</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="font-medium">Level {course.level}</span>
                      <span className="text-gray-400">•</span>
                      <span className="font-mono">{course.reference}</span>
                    </div>
                  </div>
                  <div className="ml-4 text-right">
                    <div className="text-2xl font-bold text-blue-600">{course.apprenticeCount}</div>
                    <div className="text-xs text-gray-500">
                      {course.apprenticeCount === 1 ? 'Apprentice' : 'Apprentices'}
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex gap-3">
                  <Link
                    to="/apprentices"
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    View Caseload →
                  </Link>
                  <Link
                    to={`/standards?reference=${course.reference}`}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    View Standard →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {userRole === 'instructor' && (
            <>
              <Link
                to="/apprentices"
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
              >
                <div className="font-semibold text-gray-900 mb-1">View My Caseload</div>
                <div className="text-sm text-gray-600">Manage your assigned apprentices</div>
              </Link>
              <Link
                to="/tracking"
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
              >
                <div className="font-semibold text-gray-900 mb-1">Verify OTJ Hours</div>
                <div className="text-sm text-gray-600">Review and approve training hours</div>
              </Link>
              <Link
                to="/standards"
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
              >
                <div className="font-semibold text-gray-900 mb-1">View Standards</div>
                <div className="text-sm text-gray-600">Review KSBs for your programmes</div>
              </Link>
            </>
          )}
          {userRole === 'administrator' && (
            <>
              <Link
                to="/admin"
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
              >
                <div className="font-semibold text-gray-900 mb-1">Create Course</div>
                <div className="text-sm text-gray-600">Set up new apprenticeship course</div>
              </Link>
              <Link
                to="/apprentices/new"
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
              >
                <div className="font-semibold text-gray-900 mb-1">Add Apprentice</div>
                <div className="text-sm text-gray-600">Register new apprentice</div>
              </Link>
              <Link
                to="/instructors"
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
              >
                <div className="font-semibold text-gray-900 mb-1">Manage Assessors</div>
                <div className="text-sm text-gray-600">Sync and assign assessors</div>
              </Link>
            </>
          )}
          {userRole === 'learner' && (
            <>
              <Link
                to="/my-otj"
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
              >
                <div className="font-semibold text-gray-900 mb-1">My OTJ Hours</div>
                <div className="text-sm text-gray-600">View your training progress</div>
              </Link>
              <Link
                to="/tracking/otj/new"
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
              >
                <div className="font-semibold text-gray-900 mb-1">Log Hours</div>
                <div className="text-sm text-gray-600">Record off-the-job training</div>
              </Link>
              <Link
                to="/standards"
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
              >
                <div className="font-semibold text-gray-900 mb-1">View Standards</div>
                <div className="text-sm text-gray-600">Review your KSBs</div>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
